import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  BriefcaseBusiness,
  Building2,
  Download,
  ExternalLink,
  FileSpreadsheet,
  Loader2,
  Mail,
  Search,
  Trash2,
  UsersRound,
} from 'lucide-react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import TalentOsCandidateMultiSelect from '../components/TalentOsCandidateMultiSelect.js';
import {
  useCompanyPeople,
  parseTalentOsAssignments,
  useRemoveCompanyPerson,
  useTalentOsActiveCandidates,
  useUpdateCompanyPerson,
} from '../hooks/use-api.js';
import {
  exportCompanyPeople,
  type CompanyPeopleExportFilters,
  type CompanyPersonCategory,
} from '../api.js';
import { visiblePageNumbers } from '../lib/pagination.js';
import { cn } from '../lib/utils.js';

const PAGE_SIZE = 50;
// Mirrors the 2-hour freshness window the backend's activeCompaniesOnly
// filter uses (apps/crm/src/index.ts) — two sync intervals of grace on the
// hourly TalentOS active-company sync, rather than a boolean the backend
// would need a separate step to clear when applications move on.
const ACTIVE_COMPANY_WINDOW_MS = 2 * 60 * 60 * 1000;
function isActiveTalentosCompany(activeAt: string | null | undefined): boolean {
  if (!activeAt) return false;
  const timestamp = new Date(activeAt).getTime();
  return Number.isFinite(timestamp) && Date.now() - timestamp <= ACTIVE_COMPANY_WINDOW_MS;
}

// Mirrors normalizeCompanyDirectoryName in apps/crm/src/index.ts closely
// enough for a client-side suggestion: strips common legal-entity suffixes
// so a job posting's "Foo Inc" and a LinkedIn-captured "Foo" line up as the
// same employer when matching a recruiter's current company against an
// active candidate's application company.
const COMPANY_SUFFIX_WORDS = new Set([
  'inc',
  'incorporated',
  'llc',
  'llp',
  'ltd',
  'limited',
  'corp',
  'corporation',
  'co',
  'company',
  'plc',
  'gmbh',
  'pllc',
  'lp',
  'pc',
]);
function normalizeCompanyMatchKey(value: string | null | undefined): string {
  const words = (value ?? '')
    .normalize('NFKC')
    .replace(/[^\p{L}\p{N}]+/gu, ' ')
    .trim()
    .toLowerCase()
    .split(' ')
    .filter(Boolean);
  while (words.length > 1 && COMPANY_SUFFIX_WORDS.has(words[words.length - 1]!)) {
    words.pop();
  }
  return words.join(' ');
}

const PAGE_CONFIG: Record<CompanyPersonCategory, { title: string; description: string }> = {
  recruiter: {
    title: 'Recruiters',
    description:
      'Recruiting professionals with lead-grade profile evidence, ownership, and capture history.',
  },
  hiring_manager: {
    title: 'Hiring Managers',
    description:
      'Engineering and hiring managers linked to companies and their complete profile context.',
  },
  company_leadership: {
    title: 'Company Leadership',
    description:
      'Leadership profiles for company intelligence, future research agents, and outreach.',
  },
};

export default function CompanyPeoplePage({ category }: { category: CompanyPersonCategory }) {
  // Page and search live in the URL so opening a contact and pressing Back
  // returns to the exact page the operator left, and so a view can be
  // refreshed or shared. Updates replace the history entry instead of pushing,
  // keeping the list a single entry rather than one per keystroke or page.
  const [searchParams, setSearchParams] = useSearchParams();
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [exporting, setExporting] = useState<'all' | 'selected' | null>(null);
  const [exportError, setExportError] = useState<string | null>(null);
  const selectPageCheckboxRef = useRef<HTMLInputElement>(null);
  const page = Math.max(1, Number.parseInt(searchParams.get('page') ?? '1', 10) || 1);
  const search = searchParams.get('q') ?? '';
  const candidateFilter = searchParams.get('candidateId') ?? '';
  const activeCompaniesOnly = searchParams.get('activeCompaniesOnly') === 'true';
  const capturedFrom = searchParams.get('capturedFrom') ?? '';
  const capturedTo = searchParams.get('capturedTo') ?? '';
  const sortBy = searchParams.get('sortBy') ?? '';
  const filterKey = JSON.stringify([
    category,
    search,
    candidateFilter,
    activeCompaniesOnly,
    capturedFrom,
    capturedTo,
  ]);
  const updateView = useCallback(
    (next: {
      page?: number;
      q?: string;
      candidateId?: string;
      activeCompaniesOnly?: boolean;
      capturedFrom?: string;
      capturedTo?: string;
      sortBy?: string;
    }) => {
      if (
        next.q !== undefined ||
        next.candidateId !== undefined ||
        next.activeCompaniesOnly !== undefined ||
        next.capturedFrom !== undefined ||
        next.capturedTo !== undefined
      ) {
        setSelectedIds([]);
        setExportError(null);
      }
      setSearchParams(
        (current) => {
          const params = new URLSearchParams(current);
          if (next.q !== undefined) {
            if (next.q) params.set('q', next.q);
            else params.delete('q');
          }
          if (next.candidateId !== undefined) {
            if (next.candidateId) params.set('candidateId', next.candidateId);
            else params.delete('candidateId');
          }
          if (next.activeCompaniesOnly !== undefined) {
            if (next.activeCompaniesOnly) params.set('activeCompaniesOnly', 'true');
            else params.delete('activeCompaniesOnly');
          }
          if (next.capturedFrom !== undefined) {
            if (next.capturedFrom) params.set('capturedFrom', next.capturedFrom);
            else params.delete('capturedFrom');
          }
          if (next.capturedTo !== undefined) {
            if (next.capturedTo) params.set('capturedTo', next.capturedTo);
            else params.delete('capturedTo');
          }
          if (next.sortBy !== undefined) {
            if (next.sortBy) params.set('sortBy', next.sortBy);
            else params.delete('sortBy');
          }
          const nextPage = next.page ?? 1;
          if (nextPage > 1) params.set('page', String(nextPage));
          else params.delete('page');
          return params;
        },
        { replace: true }
      );
    },
    [setSearchParams]
  );
  const setPage = useCallback(
    (updater: number | ((current: number) => number)) => {
      updateView({ page: typeof updater === 'function' ? updater(page) : updater });
    },
    [page, updateView]
  );
  const navigate = useNavigate();
  const config = PAGE_CONFIG[category];
  const { data, isLoading, isFetching, isPlaceholderData } = useCompanyPeople(
    category,
    search,
    page,
    PAGE_SIZE,
    candidateFilter,
    activeCompaniesOnly,
    capturedFrom,
    capturedTo,
    sortBy
  );
  const {
    data: candidateData,
    isLoading: candidatesLoading,
    error: candidatesError,
    refetch: refetchCandidates,
  } = useTalentOsActiveCandidates();
  const updatePerson = useUpdateCompanyPerson();
  const removePerson = useRemoveCompanyPerson();
  // Candidates already come back from useTalentOsActiveCandidates pre-filtered
  // to eligible applications only (status=active, pipeline_stage=applying,
  // stage in applied/screening/interview/offer, within the recency window —
  // see fetchEligibleTalentOsCandidates / TalentOS's crm/candidates route).
  // So any match found here is already workflow-eligible; no extra AI calls
  // or re-filtering needed, just a company-name key match.
  const candidatesByCompanyKey = useMemo(() => {
    const map = new Map<string, { id: string; name: string }[]>();
    for (const candidate of candidateData?.candidates ?? []) {
      const seenCompanyKeys = new Set<string>();
      for (const application of candidate.applications) {
        const key = normalizeCompanyMatchKey(application.job_company);
        if (!key || seenCompanyKeys.has(key)) continue;
        seenCompanyKeys.add(key);
        const bucket = map.get(key);
        if (bucket) bucket.push({ id: candidate.id, name: candidate.name });
        else map.set(key, [{ id: candidate.id, name: candidate.name }]);
      }
    }
    return map;
  }, [candidateData]);
  const people = data?.people ?? [];
  const total = data?.total ?? 0;
  const totalPages = data?.totalPages ?? 0;
  const pageNumbers = visiblePageNumbers(page, totalPages);
  const firstVisible = total > 0 ? (page - 1) * PAGE_SIZE + 1 : 0;
  const lastVisible = total > 0 ? Math.min(page * PAGE_SIZE, total) : 0;
  const selectedIdSet = useMemo(() => new Set(selectedIds), [selectedIds]);
  const selectedOnPageCount = people.reduce(
    (count, person) => count + Number(selectedIdSet.has(person.id)),
    0
  );
  const allPageSelected = people.length > 0 && selectedOnPageCount === people.length;
  const somePageSelected = selectedOnPageCount > 0 && !allPageSelected;
  const previousFilterKeyRef = useRef(filterKey);
  useEffect(() => {
    if (previousFilterKeyRef.current !== filterKey) {
      previousFilterKeyRef.current = filterKey;
      setSelectedIds([]);
      setExportError(null);
    }
  }, [filterKey]);
  useEffect(() => {
    if (selectPageCheckboxRef.current) {
      selectPageCheckboxRef.current.indeterminate = somePageSelected;
    }
  }, [allPageSelected, somePageSelected]);
  const togglePageSelection = useCallback(() => {
    const pageIds = people.map((person) => person.id);
    setSelectedIds((current) => {
      const currentSet = new Set(current);
      if (pageIds.every((id) => currentSet.has(id))) {
        for (const id of pageIds) currentSet.delete(id);
      } else {
        for (const id of pageIds) currentSet.add(id);
      }
      return [...currentSet];
    });
  }, [people]);
  const togglePersonSelection = useCallback((personId: string) => {
    setSelectedIds((current) =>
      current.includes(personId) ? current.filter((id) => id !== personId) : [...current, personId]
    );
  }, []);
  const handleExport = useCallback(
    async (mode: 'all' | 'selected') => {
      setExportError(null);
      setExporting(mode);
      try {
        const filters = Object.fromEntries(searchParams.entries()) as CompanyPeopleExportFilters;
        delete filters.page;
        delete filters.pageSize;
        delete filters.q;
        filters.category = 'recruiter';
        filters.search = search;
        filters.activeCompaniesOnly = activeCompaniesOnly;
        const result = await exportCompanyPeople(
          filters,
          mode === 'selected' ? selectedIds : undefined
        );
        if (result.rows.length === 0) {
          throw new Error(
            mode === 'selected'
              ? 'No selected recruiters still match the current filters.'
              : 'No recruiters match the current filters.'
          );
        }
        const { downloadRecruiterWorkbook } = await import('../lib/recruiterExcelExport.js');
        await downloadRecruiterWorkbook(result.rows);
      } catch (error) {
        setExportError(
          error instanceof Error ? error.message : 'The recruiter list could not be exported.'
        );
      } finally {
        setExporting(null);
      }
    },
    [activeCompaniesOnly, search, searchParams, selectedIds]
  );
  // Removing the last row on a page, or arriving on an out-of-range ?page=,
  // would otherwise strand the operator on an empty page.
  useEffect(() => {
    if (totalPages > 0 && page > totalPages) setPage(totalPages);
  }, [page, totalPages, setPage]);
  return (
    <div className="space-y-5">
      <div className="flex items-start gap-3">
        <div className="rounded-lg bg-indigo-100 p-2 text-indigo-700">
          <UsersRound size={20} />
        </div>
        <div>
          <h1 className="text-xl font-semibold text-slate-900">{config.title}</h1>
          <p className="mt-1 text-sm text-slate-500">{config.description}</p>
        </div>
        <div className="ml-auto flex items-center gap-2">
          <span className="rounded-full bg-slate-100 px-3 py-1 text-xs font-medium text-slate-600">
            {total} captured
          </span>
        </div>
      </div>
      {category === 'recruiter' && candidatesError && !candidatesLoading && (
        <div
          role="alert"
          className="flex flex-wrap items-center justify-between gap-3 rounded-lg border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-800"
        >
          <span>
            TalentOS candidates could not be loaded. Try again to refresh the recruiter options.
          </span>
          <button
            type="button"
            className="rounded-md border border-rose-300 bg-white px-3 py-1.5 font-medium hover:bg-rose-100"
            onClick={() => void refetchCandidates()}
          >
            Retry
          </button>
        </div>
      )}
      <div className="flex flex-wrap items-center gap-3">
        <div className="flex flex-1 items-center gap-2 rounded-lg border border-slate-200 bg-white px-3 py-2">
          <Search size={16} className="text-slate-400" />
          <input
            value={search}
            onChange={(e) => updateView({ q: e.target.value, page: 1 })}
            placeholder={`Search ${config.title.toLowerCase()} or company...`}
            className="flex-1 bg-transparent text-sm outline-none"
          />
        </div>
        <select
          value={candidateFilter}
          onChange={(e) => updateView({ candidateId: e.target.value, page: 1 })}
          disabled={candidatesLoading}
          className="rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm text-slate-700 disabled:opacity-50"
        >
          <option value="">All active candidates</option>
          {(candidateData?.candidates ?? []).map((candidate) => (
            <option key={candidate.id} value={candidate.id}>
              {candidate.name}
            </option>
          ))}
        </select>
        <label
          className="flex items-center gap-2 rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm text-slate-700"
          title="Only show contacts at companies where a candidate currently has an application applied, in screening, interviewing, or with an offer."
        >
          <input
            type="checkbox"
            checked={activeCompaniesOnly}
            onChange={(e) => updateView({ activeCompaniesOnly: e.target.checked, page: 1 })}
            className="rounded border-slate-300"
          />
          Active TalentOS companies only
        </label>
        <select
          value={sortBy}
          onChange={(e) => updateView({ sortBy: e.target.value, page: 1 })}
          title="Choose what order the list is shown in."
          className="rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm text-slate-700"
        >
          <option value="">Sort: Last captured</option>
          <option value="candidate_assigned">Sort: Recently added candidate</option>
        </select>
        <div
          className="flex items-center gap-2 rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm text-slate-700"
          title="Filters by when each contact was last captured/refreshed."
        >
          <span className="text-xs text-slate-500">Last captured</span>
          <input
            type="date"
            value={capturedFrom}
            onChange={(e) => updateView({ capturedFrom: e.target.value, page: 1 })}
            max={capturedTo || undefined}
            aria-label="Last captured from"
            className="rounded border border-slate-200 px-2 py-1 text-xs text-slate-700"
          />
          <span className="text-xs text-slate-400">to</span>
          <input
            type="date"
            value={capturedTo}
            onChange={(e) => updateView({ capturedTo: e.target.value, page: 1 })}
            min={capturedFrom || undefined}
            aria-label="Last captured to"
            className="rounded border border-slate-200 px-2 py-1 text-xs text-slate-700"
          />
          {(capturedFrom || capturedTo) && (
            <button
              type="button"
              onClick={() => updateView({ capturedFrom: '', capturedTo: '', page: 1 })}
              className="text-xs text-slate-400 hover:text-slate-600"
            >
              Clear
            </button>
          )}
        </div>
      </div>
      {category === 'recruiter' && (
        <div className="flex flex-wrap items-center gap-3 rounded-lg border border-slate-200 bg-white px-4 py-3">
          <div className="mr-auto text-sm text-slate-600">
            {selectedIds.length > 0 ? (
              <>
                <span className="font-semibold text-slate-900">{selectedIds.length}</span> selected
                across pages
              </>
            ) : (
              'Select recruiters on this page, or download the full filtered list.'
            )}
          </div>
          {selectedIds.length > 0 && (
            <button
              type="button"
              onClick={() => setSelectedIds([])}
              disabled={exporting !== null}
              className="rounded-md px-2 py-1.5 text-sm text-slate-500 hover:bg-slate-100 disabled:opacity-50"
            >
              Clear selection
            </button>
          )}
          <button
            type="button"
            onClick={() => void handleExport('selected')}
            disabled={selectedIds.length === 0 || exporting !== null}
            className="inline-flex items-center gap-2 rounded-md border border-indigo-200 bg-white px-3 py-2 text-sm font-medium text-indigo-700 hover:bg-indigo-50 disabled:cursor-not-allowed disabled:opacity-50"
          >
            {exporting === 'selected' ? (
              <Loader2 size={16} className="animate-spin" />
            ) : (
              <Download size={16} />
            )}
            Download selected
          </button>
          <button
            type="button"
            onClick={() => void handleExport('all')}
            disabled={total === 0 || exporting !== null}
            className="inline-flex items-center gap-2 rounded-md bg-indigo-700 px-3 py-2 text-sm font-medium text-white hover:bg-indigo-800 disabled:cursor-not-allowed disabled:opacity-50"
          >
            {exporting === 'all' ? (
              <Loader2 size={16} className="animate-spin" />
            ) : (
              <FileSpreadsheet size={16} />
            )}
            Download all filtered
          </button>
          {exportError && (
            <p role="alert" className="basis-full text-sm text-rose-700">
              {exportError}
            </p>
          )}
        </div>
      )}
      <div className="overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm">
        <div className="overflow-x-auto">
          <table className="w-full min-w-[1380px] text-left text-sm">
            <thead className="border-b border-slate-200 bg-slate-50 text-xs uppercase tracking-wide text-slate-500">
              <tr>
                {category === 'recruiter' && (
                  <th className="w-10 px-3 py-3">
                    <input
                      ref={selectPageCheckboxRef}
                      type="checkbox"
                      checked={allPageSelected}
                      disabled={people.length === 0 || isLoading || isPlaceholderData}
                      onChange={togglePageSelection}
                      aria-label="Select all recruiters on this page"
                      title="Select all recruiters on this page"
                      className="rounded border-slate-300"
                    />
                  </th>
                )}
                <th className="px-4 py-3">{category === 'recruiter' ? 'Recruiter ID' : '#'}</th>
                <th className="px-4 py-3">Person</th>
                <th className="px-4 py-3">Email</th>
                <th className="px-4 py-3">LinkedIn</th>
                <th className="px-4 py-3">Current company</th>
                <th className="px-4 py-3">Title</th>
                <th className="px-4 py-3">Location</th>
                <th className="px-4 py-3">Category</th>
                <th className="px-4 py-3">Outreach on behalf of</th>
                <th className="px-4 py-3">Completeness</th>
                <th className="px-4 py-3">Last captured</th>
                {category === 'recruiter' && <th className="px-4 py-3">Actions</th>}
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {people.map((person) => {
                const isSelected = selectedIdSet.has(person.id);
                const selectedCandidateIds = parseTalentOsAssignments(person.talentos_candidate_id);
                const companyKey = normalizeCompanyMatchKey(person.current_company_name);
                const suggestedCandidates = companyKey
                  ? (candidatesByCompanyKey.get(companyKey) ?? []).filter(
                      (candidate) => !selectedCandidateIds.includes(candidate.id)
                    )
                  : [];
                return (
                  <tr
                    key={person.id}
                    className={cn(
                      'cursor-pointer hover:bg-slate-50',
                      isSelected && 'bg-indigo-50/60'
                    )}
                    onClick={() => navigate(`/company-people/${person.id}`)}
                  >
                    {category === 'recruiter' && (
                      <td className="px-3 py-3" onClick={(e) => e.stopPropagation()}>
                        <input
                          type="checkbox"
                          checked={isSelected}
                          disabled={isPlaceholderData}
                          onChange={() => togglePersonSelection(person.id)}
                          aria-label={`Select ${person.display_name}`}
                          className="rounded border-slate-300"
                        />
                      </td>
                    )}
                    <td className="px-4 py-3">
                      {person.person_number ? (
                        <Link
                          to={`/company-people/${person.id}`}
                          onClick={(e) => e.stopPropagation()}
                          className="font-mono text-xs text-slate-500 hover:text-blue-700 hover:underline"
                          title="Open contact"
                        >
                          {person.person_number}
                        </Link>
                      ) : (
                        '—'
                      )}
                    </td>
                    <td className="px-4 py-3">
                      <Link
                        to={`/company-people/${person.id}`}
                        onClick={(e) => e.stopPropagation()}
                        className="font-medium text-indigo-700 hover:underline"
                      >
                        {person.display_name}
                      </Link>
                      {person.email && (
                        <div className="mt-0.5 truncate text-xs text-slate-500">{person.email}</div>
                      )}
                      <div className="mt-1 flex gap-1 text-[11px] text-slate-400">
                        {person.categories.map((item) => (
                          <span
                            key={item}
                            className="rounded bg-indigo-50 px-1.5 py-0.5 text-indigo-600"
                          >
                            {item.replace('_', ' ')}
                          </span>
                        ))}
                      </div>
                    </td>
                    <td className="px-4 py-3" onClick={(e) => e.stopPropagation()}>
                      {person.email ? (
                        <a
                          href={`mailto:${person.email}`}
                          className="inline-flex max-w-[240px] items-center gap-1 truncate text-indigo-600 hover:underline"
                          title={person.email}
                        >
                          <Mail size={14} className="shrink-0" />
                          <span className="truncate">{person.email}</span>
                        </a>
                      ) : (
                        <span className="text-slate-400">—</span>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      {person.linkedin_url ? (
                        <a
                          className="inline-flex items-center gap-1 text-indigo-600 hover:underline"
                          href={person.linkedin_url}
                          target="_blank"
                          rel="noreferrer"
                          onClick={(e) => e.stopPropagation()}
                        >
                          <ExternalLink size={14} />
                          Open profile
                        </a>
                      ) : (
                        <span className="text-slate-400">Unlinked</span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-slate-700">
                      <span className="inline-flex items-center gap-1">
                        <Building2 size={14} />
                        {person.current_company_name ?? 'Unlinked'}
                      </span>
                      {isActiveTalentosCompany(person.active_talentos_application_at) && (
                        <div className="mt-1">
                          <span
                            className="rounded bg-emerald-50 px-1.5 py-0.5 text-[11px] font-medium text-emerald-700"
                            title="A candidate currently has an application applied, in screening, interviewing, or with an offer at this company."
                          >
                            Active application
                          </span>
                        </div>
                      )}
                    </td>
                    <td className="px-4 py-3 text-slate-600">
                      {person.current_title ?? person.headline ?? '—'}
                    </td>
                    <td className="px-4 py-3 text-slate-600">{person.location ?? '—'}</td>
                    <td className="px-4 py-3">
                      <select
                        className="rounded border border-slate-200 bg-white px-2 py-1 text-xs"
                        value={category}
                        onClick={(e) => e.stopPropagation()}
                        onChange={(e) =>
                          updatePerson.mutate({
                            id: person.id,
                            category: e.target.value as CompanyPersonCategory,
                          })
                        }
                      >
                        <option value="recruiter">Recruiter</option>
                        <option value="hiring_manager">Hiring Manager</option>
                        <option value="company_leadership">Leadership</option>
                      </select>
                    </td>
                    <td className="px-4 py-3" onClick={(e) => e.stopPropagation()}>
                      <TalentOsCandidateMultiSelect
                        candidates={candidateData?.candidates ?? []}
                        selectedIds={selectedCandidateIds}
                        disabled={candidatesLoading || updatePerson.isPending}
                        ariaLabel={`TalentOS candidate for ${person.display_name}`}
                        loading={candidatesLoading}
                        error={Boolean(candidatesError)}
                        onChange={(ids) => {
                          const names = ids
                            .map((candidateId) =>
                              candidateData?.candidates.find(
                                (candidate) => candidate.id === candidateId
                              )
                            )
                            .filter((candidate): candidate is NonNullable<typeof candidate> =>
                              Boolean(candidate)
                            )
                            .map((candidate) => candidate.name);
                          updatePerson.mutate({
                            id: person.id,
                            talentosCandidateIds: ids,
                            talentosCandidateNames: names,
                          });
                        }}
                      />
                      {person.talentos_candidate_assigned_at && (
                        <div
                          className="mt-1 text-[11px] text-slate-400"
                          title="When a candidate was last newly added to this list."
                        >
                          Added{' '}
                          {new Date(person.talentos_candidate_assigned_at).toLocaleDateString()}
                        </div>
                      )}
                      {suggestedCandidates.length > 0 && (
                        <div className="mt-1 flex flex-wrap gap-1">
                          {suggestedCandidates.map((candidate) => (
                            <button
                              key={candidate.id}
                              type="button"
                              title={`${candidate.name} has an active application at this company. Click to add to Outreach on behalf of.`}
                              disabled={updatePerson.isPending}
                              className="rounded bg-amber-50 px-1.5 py-0.5 text-[11px] font-medium text-amber-700 hover:bg-amber-100 disabled:opacity-50"
                              onClick={() => {
                                const nextIds = [...selectedCandidateIds, candidate.id];
                                const nextNames = nextIds
                                  .map(
                                    (candidateId) =>
                                      candidateData?.candidates.find((c) => c.id === candidateId)
                                        ?.name
                                  )
                                  .filter((name): name is string => Boolean(name));
                                updatePerson.mutate({
                                  id: person.id,
                                  talentosCandidateIds: nextIds,
                                  talentosCandidateNames: nextNames,
                                });
                              }}
                            >
                              + {candidate.name}
                            </button>
                          ))}
                        </div>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        <div className="h-1.5 w-20 rounded bg-slate-200">
                          <div
                            className="h-1.5 rounded bg-indigo-500"
                            style={{ width: `${Math.min(100, person.data_completeness ?? 0)}%` }}
                          />
                        </div>
                        <span className="text-xs text-slate-500">
                          {person.data_completeness ?? 0}%
                        </span>
                      </div>
                    </td>
                    <td className="px-4 py-3 text-slate-500">
                      {person.last_captured_at
                        ? new Date(person.last_captured_at).toLocaleString()
                        : '—'}
                    </td>
                    {category === 'recruiter' && (
                      <td className="px-4 py-3" onClick={(e) => e.stopPropagation()}>
                        <button
                          type="button"
                          className="inline-flex items-center gap-1 rounded border border-red-200 px-2 py-1 text-xs text-red-600 hover:bg-red-50 disabled:opacity-50"
                          disabled={removePerson.isPending}
                          onClick={() => {
                            if (
                              window.confirm(
                                `Remove ${person.display_name} from the recruiters list? This is recoverable by restoring the record.`
                              )
                            ) {
                              removePerson.mutate(person.id);
                            }
                          }}
                        >
                          <Trash2 size={13} />
                          Remove
                        </button>
                      </td>
                    )}
                  </tr>
                );
              })}
              {!isLoading && people.length === 0 && (
                <tr>
                  <td
                    colSpan={category === 'recruiter' ? 13 : 11}
                    className="px-4 py-12 text-center text-slate-400"
                  >
                    <BriefcaseBusiness className="mx-auto mb-2" size={24} />
                    No {config.title.toLowerCase()} captured yet.
                  </td>
                </tr>
              )}
              {isLoading && (
                <tr>
                  <td
                    colSpan={category === 'recruiter' ? 13 : 11}
                    className="px-4 py-12 text-center text-slate-400"
                  >
                    Loading...
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
      <div className="flex flex-wrap items-center justify-between gap-3 py-2">
        <div className="flex items-center gap-3 text-sm text-slate-500">
          <span>
            Showing {firstVisible}–{lastVisible} of {total}
          </span>
          {isFetching && !isLoading && (
            <span className="inline-flex items-center gap-1 text-blue-600">
              <Loader2 size={14} className="animate-spin" /> Updating
            </span>
          )}
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <button
            type="button"
            onClick={() => setPage(1)}
            disabled={page <= 1}
            className="rounded-md border border-slate-200 bg-white px-3 py-1.5 text-sm hover:bg-slate-50 disabled:opacity-40"
          >
            First
          </button>
          <button
            type="button"
            onClick={() => setPage((current) => Math.max(1, current - 1))}
            disabled={page <= 1}
            className="rounded-md border border-slate-200 bg-white px-3 py-1.5 text-sm hover:bg-slate-50 disabled:opacity-40"
          >
            Previous
          </button>
          <div className="flex items-center gap-1" aria-label={`${config.title} result pages`}>
            {pageNumbers.map((pageNumber) => (
              <button
                key={pageNumber}
                type="button"
                onClick={() => setPage(pageNumber)}
                aria-current={pageNumber === page ? 'page' : undefined}
                className={cn(
                  'min-w-8 rounded-md border px-2 py-1.5 text-sm',
                  pageNumber === page
                    ? 'border-blue-600 bg-blue-600 font-semibold text-white'
                    : 'border-slate-200 bg-white text-slate-700 hover:bg-slate-50'
                )}
              >
                {pageNumber}
              </button>
            ))}
          </div>
          <span className="text-sm text-slate-500">of {totalPages}</span>
          <button
            type="button"
            onClick={() => setPage((current) => Math.min(totalPages, current + 1))}
            disabled={totalPages === 0 || page >= totalPages}
            className="rounded-md border border-slate-200 bg-white px-3 py-1.5 text-sm hover:bg-slate-50 disabled:opacity-40"
          >
            Next
          </button>
          <button
            type="button"
            onClick={() => setPage(totalPages)}
            disabled={totalPages === 0 || page >= totalPages}
            className="rounded-md border border-slate-200 bg-white px-3 py-1.5 text-sm hover:bg-slate-50 disabled:opacity-40"
          >
            Last
          </button>
        </div>
      </div>
    </div>
  );
}
